import React, { Fragment, useCallback, useState } from 'react';
import { classes } from '../../../utils/commonUtils';
import { Anchor } from '../../Link/Link';
import { MotionModal } from '../../motion-modal/motion-modal';
import { LinkModalCheckbox } from './link-modal-checkbox';
import css from './link-modal.module.scss';
import { useDontShowAgain } from './use-dont-show-again';

export const DEEPLINK_MODAL_STORAGE_KEY = 'deeplink-modal';

interface LinkModalProps {
  children: JSX.Element;
  id: string;
  title: string;
  description: string;
  /**
   * Optional function to call when the modal is opened.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  primaryButton: {
    title: string;
    url: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  };
  secondaryButton?: {
    title: string;
    url: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  };
  onClose?: () => void;
  initialOpen?: boolean;
}

export const LinkModal = React.forwardRef<HTMLAnchorElement, LinkModalProps>(
  (
    { children, id, title, description, onClick, primaryButton, secondaryButton, initialOpen = false, onClose },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const { isChecked, check } = useDontShowAgain(id);

    const close = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isChecked) {
          onClick?.(event);
          children.props.onClick?.();
          return;
        }

        event.preventDefault();
        onClick?.(event);
        setIsOpen(true);
        return false;
      },
      [children.props, isChecked, onClick],
    );

    const handlePrimaryClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        close();
        primaryButton.onClick?.(event);
      },
      [close, primaryButton],
    );

    const handleSecondaryClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        close();
        secondaryButton?.onClick?.(event);
      },
      [close, secondaryButton],
    );

    return (
      <Fragment>
        <MotionModal classNames={{ content: css.modal }} isOpen={isOpen} onRequestClose={close}>
          <div className={css.container} onClick={(e) => e.stopPropagation()}>
            <MotionModal.CloseButton onClick={close} />

            <h3 className={css.title}>{title}</h3>

            <p className={css.description}>{description}</p>

            <LinkModalCheckbox handleCheckmark={(event) => check(event.target.checked)} checked={isChecked} />

            <div className={css.buttons}>
              <Anchor
                className={classes(css.button, css.primary)}
                onClick={handlePrimaryClick}
                href={primaryButton.url}
                target="_blank"
                rel="noreferrer"
              >
                {primaryButton.title}
              </Anchor>
              {secondaryButton !== undefined && (
                <Anchor
                  className={css.button}
                  onClick={handleSecondaryClick}
                  href={secondaryButton.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {secondaryButton.title}
                </Anchor>
              )}
            </div>
          </div>
        </MotionModal>

        {React.cloneElement(children, {
          onClick: handleClick,
        })}
      </Fragment>
    );
  },
);
